import type { Distance, SpatialRectangle } from "@pdfking/math"
import { AddMargins, Floor, RemoveMargins, Round, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { ToPixel } from "@pdfking/quantity"
import type { ColorID, PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"

import { PALETTE, PUBLISHER, TITLE } from "#constants"
import type { DrawPage } from "#internal"

import { DrawPlate } from "../layout/DrawPlate"
import { PageMetrics } from "../layout/PageMetrics"
import { PosterBox } from "../layout/PosterBox"
import { DrawPoster } from "../poster/DrawPoster"
import { CreateRandom } from "../random/CreateRandom"
import { DrawProse } from "../text/DrawProse"
import { LoremSentence } from "../text/LoremSentence"
import { LoremTitle } from "../text/LoremWords"
import { PosterWord } from "../text/PosterWord"
import { WrapLines } from "../text/WrapLines"
import { BookPageKind, ChapterNumber, ChapterQ } from "./BookPageKind"

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"]
const roman = (n: number) => ROMAN[n - 1] ?? `${n}`

// A page of a lorem book of `size`, its corner at the origin, its text within
// `security`, the running head at its top and the folio at its foot; the
// paper runs on into `bleed`. The spot
// marks the chapter with a thumb tab on the outer edge, lower chapter by
// chapter. Laid out from the top down, as a page is read: every place is how
// far below the top it is
export const DrawBookPage: DrawPage = ({
  bleed,
  labels,
  measure,
  page_number,
  security,
  seed,
  size,
}) => {
  const { height, width } = size
  const page = { ...size, x: ZERO_DISTANCE, y: ZERO_DISTANCE }
  const shapes: PageShape[] = [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.paper, position: AddMargins(page, bleed) },
  ]
  const kind = BookPageKind(seed, page_number)

  const { font_size, short } = PageMetrics(size)
  const frame = RemoveMargins(page, security)
  const middle = Total(frame.x, Scale(frame.width, 0.5))
  const line_height = Scale(font_size, 1.45)
  const random = CreateRandom(seed, "page", page_number)
  const chapter = ChapterNumber(seed, page_number)
  const chapter_title = LoremTitle(CreateRandom(seed, "chapter title", chapter), 3)
  // Where a place `below` the top is, the page's y
  const top = (below: Distance) => Subtract(height, below)

  // A line of text whose top is `below` the top of the page: centered in the
  // frame, or set from `left`
  const text = ({
    below,
    color = PALETTE.text,
    left,
    size,
    value,
  }: {
    below: Distance
    color?: ColorID
    left?: Distance
    size: Distance
    value: string
  }) =>
    shapes.push({
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(size),
      horizontal_align: left ? HorizontalAlign.LEFT : HorizontalAlign.CENTER,
      position: {
        height: size,
        width: left ? Subtract(Total(frame.x, frame.width), left) : frame.width,
        x: left ?? frame.x,
        y: Subtract(top(below), size),
      },
      text: value,
      text_color: color,
      vertical_align: VerticalAlign.TOP,
    })
  // A short rule centered in the frame, its top `below` the top of the page
  const rule = ({
    below,
    color = PALETTE.primary,
    half_width,
  }: {
    below: Distance
    color?: ColorID
    half_width: Distance
  }) => {
    const thickness = Scale(short, 0.003)
    shapes.push({
      enum: PlotFunction.PLOT_RECT,
      fill_color: color,
      position: {
        height: thickness,
        width: Scale(half_width, 2),
        x: Subtract(middle, half_width),
        y: Subtract(top(below), thickness),
      },
    })
  }
  // The prose's words onto the page, in `box`; answers the top of the line it
  // stopped at, the page's y
  const prose = (
    box: SpatialRectangle<Distance>,
    first_paragraph_indent?: (line: number) => Distance
  ) => {
    const [words, end] = DrawProse({
      box,
      color: PALETTE.text,
      first_paragraph_indent,
      font_size,
      line_height,
      measure,
      random,
    })
    shapes.push(...words)
    return end
  }

  // The thumb tab: a sixth of the text height, out into the bleed on the
  // outer edge, right on a right-hand (odd) page, left on a left-hand one
  if (kind !== "title") {
    const tab_width = Scale(short, 0.03)
    const tab_height = Scale(frame.height, 1 / 6)
    const below = Total(security.top, Scale(tab_height, (((chapter - 1) % 6) + 6) % 6))
    const y = Subtract(top(below), tab_height)
    shapes.push({
      enum: PlotFunction.PLOT_RECT,
      fill_color: PALETTE.spot,
      position:
        page_number % 2
          ? {
              height: tab_height,
              width: Total(tab_width, bleed.right),
              x: Subtract(width, tab_width),
              y,
            }
          : {
              height: tab_height,
              width: Total(tab_width, bleed.left),
              x: Scale(bleed.left, -1),
              y,
            },
    })
  }
  // The text block: under the running head, above the folio, each as far
  // from it
  const head = Scale(line_height, 2.2)
  const body: SpatialRectangle<Distance> = {
    height: Subtract(frame.height, Scale(head, 2)),
    width: frame.width,
    x: frame.x,
    y: Total(frame.y, head),
  }
  const body_top = Total(body.y, body.height)
  // `body` from `y` down
  const body_from = (y: Distance): SpatialRectangle<Distance> => ({
    ...body,
    height: Subtract(y, body.y),
  })

  if (kind === "title") {
    // The title between its two rules, centered in the frame
    const gap = Scale(frame.height, 0.04)
    const block = Total(gap, Scale(short, 0.2))
    const at = Total(security.top, Scale(Subtract(frame.height, block), 0.5))
    const title = Total(at, gap)
    rule({ below: at, half_width: Scale(short, 0.04) })
    text({ below: title, size: Scale(short, 0.09), value: TITLE })
    text({
      below: Total(title, Scale(short, 0.13)),
      size: Scale(font_size, 1.3),
      value: LoremTitle(random, 4),
    })
    rule({ below: Total(title, Scale(short, 0.2)), half_width: Scale(short, 0.04) })
    text({
      below: Subtract(height, Total(security.bottom, Scale(font_size, 0.8))),
      size: Scale(font_size, 0.8),
      value: PUBLISHER,
    })
    return shapes
  }

  // Folio: the page number on the foot of the frame, under a short rule, as
  // the running head is over its own
  rule({
    below: Subtract(height, Total(security.bottom, Scale(line_height, 1.1))),
    half_width: Scale(short, 0.015),
  })
  text({
    below: Subtract(height, Total(security.bottom, Scale(font_size, 0.85))),
    size: Scale(font_size, 0.85),
    value: `${page_number}`,
  })
  // The page left before a chapter has nothing else
  if (kind === "blank") return shapes

  if (kind === "chapter") {
    const opening = Scale(height, 0.18)
    text({
      below: opening,
      color: PALETTE.primary,
      size: Scale(font_size, 0.8),
      value: labels.CHAPTER(),
    })
    text({
      below: Total(opening, Scale(font_size, 1.6)),
      color: PALETTE.primary,
      size: Scale(short, 0.14),
      value: roman(chapter),
    })
    text({
      below: Total(opening, Scale(short, 0.22)),
      size: Scale(font_size, 1.5),
      value: chapter_title,
    })
    rule({
      below: Total(opening, Scale(short, 0.3)),
      color: PALETTE.secondary,
      half_width: Scale(short, 0.06),
    })
    // A drop cap three lines tall in the first paragraph
    const start = Total(opening, Scale(short, 0.36))
    const cap = String.fromCharCode(65 + ((chapter * 7) % 26))
    const cap_size = Scale(line_height, 3)
    const cap_width = Total(measure(cap, cap_size), Scale(font_size, 0.4))
    text({
      below: Subtract(start, Scale(line_height, 0.15)),
      color: PALETTE.primary,
      left: frame.x,
      size: cap_size,
      value: cap,
    })
    prose(body_from(top(start)), line => (line < 3 ? cap_width : ZERO_DISTANCE))
    return shapes
  }

  // Running head: the book on the left pages, the chapter on the right ones
  // over a short rule, like the folio's
  const running_head = page_number % 2 === 0 ? TITLE : chapter_title
  text({ below: security.top, size: Scale(font_size, 0.7), value: running_head.toUpperCase() })
  rule({ below: Total(security.top, Scale(line_height, 1.1)), half_width: Scale(short, 0.015) })

  if (kind === "quote") {
    // A pull quote, centered in the text block: a big primary mark, the
    // quote large, a short rule and who said it
    const size = Scale(font_size, 2)
    const leading = Scale(size, 1.3)
    const lines = WrapLines({
      measure,
      size,
      text: LoremSentence(random, 10 + Floor(random() * 8)),
      width: Scale(body.width, 0.8),
    })
    const mark = Scale(short, 0.22)
    const block = Total(Scale(mark, 0.6), Scale(leading, lines.length), Scale(line_height, 2.5))
    let below = Total(top(body_top), Scale(Subtract(body.height, block), 0.5))
    text({
      below: Subtract(below, Scale(mark, 0.15)),
      color: PALETTE.primary,
      size: mark,
      value: "“",
    })
    below = Total(below, Scale(mark, 0.6))
    lines.forEach((value, i) => text({ below: Total(below, Scale(leading, i)), size, value }))
    below = Total(below, Scale(leading, lines.length), Scale(line_height, 0.8))
    rule({ below, half_width: Scale(short, 0.03) })
    text({
      below: Total(below, Scale(line_height, 0.6)),
      size: Scale(font_size, 0.8),
      value: LoremTitle(random, 2).toUpperCase(),
    })
    return shapes
  }

  if (kind === "image") {
    // A poster the shape of the page, centered in the frame, its caption
    // right under it
    const plate = PosterBox(frame, size)
    shapes.push(
      ...DrawPlate({
        box: plate,
        draw: inside =>
          DrawPoster({
            box: inside,
            measure,
            panels: 1,
            random,
            word: PosterWord(seed, page_number),
            word_panel: 0,
          }),
        short,
      })
    )
    text({
      below: Total(top(plate.y), Scale(line_height, 0.6)),
      size: Scale(font_size, 0.85),
      value: `Fig. ${page_number}. ${LoremTitle(random, 3)}`,
    })
    return shapes
  }

  // The top of the text block, down to somewhere between 30% and 70% of it,
  // on a line
  const part_way = (): SpatialRectangle<Distance> => {
    const part = Scale(line_height, Round((body.height / line_height) * (0.3 + random() * 0.4)))
    return { ...body, height: part, y: Subtract(body_top, part) }
  }
  // The chapter's last page: its text runs out part way down
  if (ChapterQ(seed, page_number + 2)) {
    prose(part_way())
    return shapes
  }
  if (random() > 0.3) {
    prose(body)
    return shapes
  }
  // Now and then a section break: three small squares a line tall
  const end = prose(part_way())
  const square = Scale(font_size, 0.3)
  for (const offset of [-1, 0, 1])
    shapes.push({
      enum: PlotFunction.PLOT_RECT,
      fill_color: PALETTE.primary,
      position: {
        height: square,
        width: square,
        x: Total(middle, Scale(square, 3 * offset - 0.5)),
        y: Subtract(end, Total(line_height, Scale(square, 0.5))),
      },
    })
  prose(body_from(Subtract(end, Scale(line_height, 2))))
  return shapes
}
